<template>
  <div>
    <DoughnutChart :chart-data="chartData" :options="computedChartOptions"
      :style="{ width: chartWidth, height: chartHeight }" :key="chartKey" />
  </div>
</template>

<script>
import { defineComponent, ref } from "vue";
import { DoughnutChart } from "vue-chart-3";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

// ── Module-level registration so HMR always picks up the latest code ──────────
if (Chart.registry.plugins.get('centerText')) {
  Chart.unregister(Chart.registry.plugins.get('centerText'));
}

Chart.register({
  id: "centerText",
  afterDraw: (chart) => {
    const { ctx } = chart;
    const options = chart.options?.plugins?.centerText || {};
    const title = String(options.title ?? "");
    const subtitle = options.subtitle || "";
    const ChartColor = options.ChartColor || "black";
    const titleStyle = options.centerTitleStyle || {};
    const subtitleStyle = options.centerSubtitleStyle || {};
    const titleOffsetY = options.titleOffsetY ?? 0;
    const subtitleOffsetY = options.subtitleOffsetY ?? 25;

    ctx.save();

    // Get the EXACT center of the rendered donut ring
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || !meta.data[0]) { ctx.restore(); return; }
    const centerX = meta.data[0].x;
    const centerY = meta.data[0].y;

    const fontBase = '"Poppins", Arial, sans-serif';
    const titleFontSize = parseInt(titleStyle.fontSize || '28px');
    const bigFont = `${titleStyle.fontWeight || 'bold'} ${titleStyle.fontSize || '28px'} ${fontBase}`;

    // Use alphabetic baseline for precise character-based vertical centering
    ctx.textBaseline = 'alphabetic';
    ctx.font = bigFont;
    ctx.fillStyle = ChartColor;

    if (title.endsWith('%')) {
      const numberPart = title.slice(0, -1);
      const percentPart = '%';
      const percentFontSize = Math.round(titleFontSize * 0.55) + 'px';
      const smallFont = `${titleStyle.fontWeight || 'bold'} ${percentFontSize} ${fontBase}`;

      // Measure the number for precise centering
      const metrics = ctx.measureText(numberPart);
      // Visual middle offset: (ascent - descent) / 2
      // We want the literal middle of the ink to be at centerY
      const visualMiddleOffset = (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;

      const numberY = centerY + visualMiddleOffset + titleOffsetY;

      // Draw "56" centered horizontally and vertically
      ctx.textAlign = 'center';
      ctx.fillText(numberPart, centerX, numberY);

      // Draw "%" relative to "56"
      ctx.textAlign = 'left';
      ctx.font = smallFont;
      const numberWidth = metrics.width;
      
      // Calculate vertical middle for percent sign separately
      const percentMetrics = ctx.measureText(percentPart);
      const percentVisualMiddleOffset = (percentMetrics.actualBoundingBoxAscent - percentMetrics.actualBoundingBoxDescent) / 2;
      const percentY = centerY + percentVisualMiddleOffset + titleOffsetY;
      
      ctx.fillText(percentPart, centerX + numberWidth / 2 + 2, percentY);

      // Subtitle "Score" - position below the number
      if (subtitle) {
        ctx.textAlign = 'center';
        ctx.font = `${subtitleStyle.fontWeight || '500'} ${subtitleStyle.fontSize || '14px'} ${fontBase}`;
        ctx.fillStyle = "grey";
        // Place subtitle below the number's descent + an extra offset
        const subtitleY = numberY + metrics.actualBoundingBoxDescent + subtitleOffsetY;
        ctx.fillText(subtitle, centerX, subtitleY);
      }

    } else {
      const metrics = ctx.measureText(title);
      const visualMiddleOffset = (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2;
      const titleY = centerY + visualMiddleOffset + titleOffsetY;

      ctx.textAlign = 'center';
      ctx.fillText(title, centerX, titleY);

      if (subtitle) {
        ctx.textAlign = 'center';
        ctx.font = `${subtitleStyle.fontWeight || '500'} ${subtitleStyle.fontSize || '14px'} ${fontBase}`;
        ctx.fillStyle = "grey";
        const subtitleY = titleY + metrics.actualBoundingBoxDescent + subtitleOffsetY;
        ctx.fillText(subtitle, centerX, subtitleY);
      }
    }

    ctx.restore();
  },
});
// ─────────────────────────────────────────────────────────────────────────────

export default defineComponent({
  name: "DoughnutChartComp",
  components: { DoughnutChart },
  props: {
    chartData: Object,
    options: Object,
    chartWidth: String,
    chartHeight: String,
    ChartColor: {
      type: String,
    },
    centerTitle: {
      type: [String, Number],
      required: true,
    },
    centerSubtitle: {
      type: String,
      required: false,
      default: "",
    },
    centerTitleStyle: {
      type: Object,
      default: () => ({ fontSize: '28px', fontWeight: 'bold' })
    },
    centerSubtitleStyle: {
      type: Object,
      default: () => ({ fontSize: '14px', fontWeight: 'bold' })
    },
    titleOffsetY: {
      type: Number,
      default: 0
    },
    subtitleOffsetY: {
      type: Number,
      default: 25
    }
  },
  setup(props) {
    const chartKey = ref(0);

    const computedChartOptions = {
      cutout: '80%',
      ...props.options,
      plugins: {
        ...props?.options?.plugins,
        centerText: {
          title: props.centerTitle,
          subtitle: props.centerSubtitle,
          ChartColor: props.ChartColor,
          centerTitleStyle: props.centerTitleStyle,
          centerSubtitleStyle: props.centerSubtitleStyle,
          titleOffsetY: props.titleOffsetY,
          subtitleOffsetY: props.subtitleOffsetY,
        },
      },
      layout: {
        padding: { left: 0, right: 0, top: 0, bottom: 0 },
      },
    };

    return {
      computedChartOptions,
      chartKey,
    };
  },
});
</script>

<style>
canvas {
  margin: 0px;
  padding: 0px;
}
</style>
